import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import * as simsApi from '../api/simulations';

// Saving/history has no cap — the metered resource is compute (an
// "expanded" simulation run, gated server-side in simulation-service via
// actor-service's quota API), not storage. See AuthContext's user.plan /
// user.freeSimCreditsUsed / user.freeSimCreditsLimit for quota display.
export function useSimulationHistory() {
  const { user } = useAuth();
  const token  = user?.token;
  const userId = user?.id;

  const [sims,    setSims]    = useState([]);
  // Starts true, not false — ConsolePage shows a spinner in place of
  // SimulationsTab while this is true. Starting it false let SimulationsTab
  // mount immediately on first render (before the fetch effect had even set
  // loading:true), then get unmounted the instant it did, then remount fresh
  // once it settled back to false — which silently ate the one-time
  // sessionStorage prefill from Beyond CBM's "Run in console" handoff on
  // that first, throwaway mount. Starting true means SimulationsTab only
  // ever mounts once, after data's ready.
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!token || !userId) return;
    setLoading(true);
    try {
      const simList = await simsApi.listSimulations(token, userId);
      setSims(simList);
    } catch {
      // Silently fall through — UI will show empty state
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addSimulation = useCallback(async (record) => {
    if (!token || !userId) return;
    const saved = await simsApi.saveSimulation(record, token, userId);
    setSims((prev) => [saved, ...prev]);
    return saved;
  }, [token, userId]);

  const removeSimulation = useCallback(async (id) => {
    if (!token || !userId) return;
    await simsApi.deleteSimulation(id, token, userId);
    setSims((prev) => prev.filter((s) => s._id !== id));
  }, [token, userId]);

  return {
    sims,
    loading,
    addSimulation,
    removeSimulation,
    refetch: fetchAll,
  };
}
